<template>
  <v-layout>
    <v-flex>
      <v-row no-gutters>
        <v-col class="text-left ml-n6" cols="12" md="6">
          <v-breadcrumbs :items="subNavs"></v-breadcrumbs>
        </v-col>
        <v-col class="text-right"  cols="12" md="6">
          <ActionButtons
            @doClose="doClose"
            @doPrint="doPrint"
            @doEdit="doEdit"
            :actionBtnSettings="actionBtnSettings"
          >
          </ActionButtons>
        </v-col>
      </v-row>

      <v-card class="pa-6 mt-3">
        <v-card-title>
          Invoice
          <v-spacer></v-spacer>
          <v-btn
            v-if="invoice.status == $getConstInvoiceStatus('INVOICE_STATUS_SENT')"
            color="primary mr-6"
            depressed
            rounded
            @click="paymentMade(invoice)">
            <span v-if="loading" class="mx-4">Please wait...</span>
            <span v-if="!loading" class="mx-4">Payment Made</span>
          </v-btn>
          <InvoiceStatus :status="invoice.status" :statuses="statuses"></InvoiceStatus>
        </v-card-title>
        <v-card-subtitle>
          #{{invoice.invoice_no}}
          <v-spacer></v-spacer>
        </v-card-subtitle>
        <v-card-text class="text--primary mt-6">

          <v-row no-gutters>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-account-outline
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.bill_to_name}}</v-list-item-title>
                        <v-list-item-subtitle>Bill To</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-map-marker-outline
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.bill_to_address}}</v-list-item-title>
                        <v-list-item-subtitle>Address</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-phone-classic
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.bill_to_contact_no}}</v-list-item-title>
                        <v-list-item-subtitle>Contact No.</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-receipt-text-outline
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.ref_no ? invoice.ref_no : '-'}}</v-list-item-title>
                        <v-list-item-subtitle>Reference No.</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ invoice.invoice_date | moment("MMM D, YYYY") }}</v-list-item-title>
                        <v-list-item-subtitle>Invoice Date</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ invoice.due_date | moment("MMM D, YYYY") }}</v-list-item-title>
                        <v-list-item-subtitle>Due Date</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-data-table
            class="mt-10"
            :headers="headers"
            :items="items"
            :loading="loading"
            :items-per-page="1000"
            hide-default-footer
            loading-text="Loading Items..."
            no-data-text="No Items"
            ref="table">

            <template v-slot:[`item.description`]="{ item }">
              <div v-if="item.container" class="font-weight-bold">
                {{ item.container }}
              </div>
              <v-icon
                v-if="item.shipment_type"
                size="24"
                class="mr-2">
                {{ getShipmentIcon(item) }}
              </v-icon>
              {{ item.description }}
            </template>

            <template v-slot:[`item.measure`]="{ item }">
              <div v-if="item.shipment_type">
                <span v-if="isShipmentForSea(item.shipment_type)">{{formatNumber(item.measure) }} CBM</span>
                <span v-if="!isShipmentForSea(item.shipment_type)">{{formatNumber(item.measure) }} KG</span>
              </div>
            </template>

            <template v-slot:[`item.qty`]="{ item }">
              {{ formatNumber(item.qty) }}
            </template>

            <template v-slot:[`item.amount`]="{ item }">
              {{ formatNumberAmount($numeral, item.amount) }}
            </template>

            <template v-slot:[`item.rate`]="{ item }">
              {{ formatNumberAmount($numeral, item.rate) }}
            </template>

            <template v-slot:body.append>
              <tr class="sm-hidden">
                <td class="text-right"><strong>TOTAL</strong></td>
                <td class="text-center">
                  <strong>{{ formatNumber(totalQty) }}</strong>
                </td>
                <td class="text-center">
                  <strong>{{ formatNumber(totalCbm) }}</strong>
                </td>
                <td colspan="2"></td>
              </tr>
            </template>

          </v-data-table>
          <v-divider></v-divider>

          <v-row class="mt-6">
            <v-col cols="12" sm="12" md="5">
              <div class="text-subtitle-2" v-if="invoice.remarks">
                Remarks
                <br>
                <span class="text-caption">{{invoice.remarks}}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="3" offset-md="4">
              <v-card-title class="text-caption pa-0">
                Subtotal
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">{{formatNumberAmount($numeral, invoice.subtotal)}}</span>
              </v-card-title>

              <v-card-title class="text-caption pa-0 py-4">
                Discounts
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">- {{formatNumberAmount($numeral, invoice.discounts)}}</span>
              </v-card-title>

              <v-card-title class="text-caption pa-0 pb-4">
                Add VAT 12%
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">{{formatNumberAmount($numeral, invoice.add_vat_amount)}}</span>
              </v-card-title>

              <v-card-title class="text-caption pa-0 pb-4">
                Less Withholding Tax
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">- {{formatNumberAmount($numeral, invoice.less_withholding_tax_amount)}}</span>
              </v-card-title>
              <v-card-title class="text-caption pa-0 pb-4">
                <strong>Points Redeemed</strong>
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">- {{formatNumberAmount($numeral, invoice.points_redeemed)}}</span>
              </v-card-title>
              <v-card-title class="text-caption pa-0">
                Grand Total
                <v-spacer></v-spacer>
                <span class="text-subtitle-1 font-weight-bold">{{formatNumberAmount($numeral, grandTotal)}}</span>
              </v-card-title>

            </v-col>
          </v-row>

        </v-card-text>

        <v-card-title class="mt-12">
          Proof of Payments
          <v-chip
            text-color="white"
            class="ml-6"
            @click="clickFile()"
            color="primary">
            <v-icon small left>
              mdi-image-plus
            </v-icon>
            <strong>Upload Image</strong>
          </v-chip>
          <input v-show="false" type="file" id="file" ref="file" multiple v-on:change="onChangeFileUpload()" accept="image/png, image/jpeg, image/jpg"/>
        </v-card-title>
        <v-card-text>
          <AlertMessage :alerMessage="alerMessage"></AlertMessage>

          <v-row class="mt-6">
            <v-col
              v-for="img in invoiceImages"
              :key="img.id"
              class="d-flex child-flex"
              cols="4"
            >
              <v-img
                :src="img.loc"
                :lazy-src="img.loc"
                aspect-ratio="1"
                class="grey lighten-2"
              >
                <div class="float-right mr-2 mt-2 mb-2">
                  <v-btn
                    class="mr-2"
                    color="grey"
                    fab
                    small
                    dark
                    depressed
                    @click="showImg(img.loc)"
                  >
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>

                  <v-btn
                    color="red"
                    fab
                    small
                    dark
                    depressed
                    @click="confirmDeleteImg(img)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-5"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <SnackBar :snackbarDetails="snackbarDetails"></SnackBar>
      <ConfirmationDialog
        :dialog="dialog"
        @doConfirm="doConfirm"
        @doCancel="doCancel"
      >
      </ConfirmationDialog>
      <Loader :loading="loading"></Loader>
    </v-flex>
  </v-layout>
</template>

<script>
  import SnackBar from '@/components/snackbar/SnackBar.vue'
  import Loader from '@/components/Loader.vue'
  import { mapGetters } from "vuex";
  import ActionButtons from '@/components/ActionButtons.vue'
  import AlertMessage from '@/components/AlertMessage.vue'
  import InvoiceStatus from '@/components/InvoiceStatus.vue'
  import ConfirmationDialog from '@/components/dialog/ConfirmationDialog.vue'

  export default {
    name: 'InvoiceView',
    data: () => ({
      id: null,
      accountingNamespace: 'accounting',
      loading: false,
      print: false,
      isLoading: false,
      deletedItems: [],
      perPage: 2000,
      actionBtnSettings: {
        edit: 1,
        save: 0,
        refresh: 0,
        close: 1,
        print: 1,
        send: 0,
        save_and_print: 0,
        save_disabled: false,
        close_disabled: false,
        print_disabled: false,
        save_and_print_disabled: false
      },
      alerMessage:{
        show: false,
        status: '',
        type: 'error',
        omitMessage: '',
        messages: []
      },
      snackbarDetails: {
        color: 'sb-success',
        msg: '',
        show: false
      },
      dialog: {
        show: false,
        title: 'Delete Item',
        body: 'Are you sure you want to delete this item?',
        type: '',
        item: {},
        confirmBtn: 'Yes',
        cancelBtn: 'Cancel'
      },
      invoice: {
        id: '',
        invoice_no: '',
        ref_no: '',
        bill_to_name: '',
        bill_to_contact_no: '',
        bill_to_address: '',
        invoice_date: '',
        remarks: '',
        sub_total: 0,
        discounts: 0,
        other_charges: 0,
        net_amount: 0
      },
      statuses: [],
      subNavs: [
        {
          text: 'Invoices',
          disabled: false,
          exact: true,
          to: {
            name: 'invoices'
          },
        },
        {
          text: 'View Invoice',
          disabled: true,
          href: 'breadcrumbs_link_2',
        },
      ],
      imgUploading: false,
      invoiceImages: [],
      items: [],
      headers: [
        {
          text: 'Item',
          align: 'start',
          value: 'description',
          sortable: false
        },
        {
          text: 'Qty',
          align: 'center',
          value: 'qty',
          sortable: false,
          width: 180
        },
        {
          text: 'Measure',
          align: 'center',
          value: 'measure',
          sortable: false,
          width: 180
        },
        {
          text: 'Rate',
          align: 'center',
          value: 'rate',
          sortable: false,
          width: 180
        },
        {
          text: 'Amount',
          align: 'right',
          value: 'amount',
          sortable: false,
          width: 180
        }
      ]
    }),
    watch: {
    },
    computed: {
      ...mapGetters(['formatNumberAmount', 'formatNumber', 'formatNumberAmountNoComma', 'getCurrentUser', 'forSea']),
      totalQty: function(){
        let qty = 0

        this.items.forEach(obj => {
          qty = qty + Number(this.formatNumberAmountNoComma(this.$numeral, obj.qty))
        })
        return qty
      },
      totalCbm: function(){
        let cbm = 0
        this.items.forEach(obj => {
          cbm = cbm + Number(this.formatNumberAmountNoComma(this.$numeral, obj.measure))
        })
        return cbm
      },
      grandTotal: function(){
        return Number(this.formatNumberAmountNoComma(this.$numeral, this.invoice.grand_total))  - Number(this.formatNumberAmountNoComma(this.$numeral, this.invoice.points_redeemed))
      }
    },
    methods: {
      filter: function(evt) {
        evt = (evt) ? evt : window.event;
        let expect = evt.target.value.toString() + evt.key.toString();

        if (!/^[-+]?[0-9]*\.?[0-9]*$/.test(expect)) {
          evt.preventDefault();
        } else {
          return true;
        }
      },
      deleteImg () {
        self.loading = true

        this.$http.secured.post(this.accountingNamespace+'/invoices/confirmation-images/delete/'+this.dialog.item.id, '')
          .then(response => {
            this.getInvoiceImages()
            this.showMessage("Image has been successfully deleted.", 'success')
          })
          .catch(error => {
            if (error.response) {
              this.showMessage(error.response.data.messages, 'error')
            }else{
              this.showMessage("Something went wrong! Please try again.", 'error')
            }
          })
          .then(function () {
            self.loading = false
          })
      },
      confirmDeleteImg (img) {
        this.dialog = {
          show: true,
          title: 'Delete Image',
          body: 'Are you sure you want to delete this image?',
          type: 'confirm-remove-img',
          item: img,
          confirmBtn: 'Yes',
          cancelBtn: 'No'
        }
      },
      showImg (imgLoc) {
        window.open(imgLoc, '_newtab');
      },
      clickFile () {
        let file = document.getElementById('file');
        file.click()
      },
      getInvoiceImages () {
        let self = this
        this.$http.plain.get(this.accountingNamespace+'/invoices/confirmation-images/'+this.id)
          .then(response => {
            this.invoiceImages = response.data
          })
          .catch(error => {
            console.log(error);
          })
      },
      onChangeFileUpload (){
        this.imgUploading = true
        this.alerMessage.show = false

        let self = this
        let formData = new FormData();

        for (let i = 0; i < this.$refs.file.files.length; i++) {
          formData.append('files[]', this.$refs.file.files[i]);
        }

        this.$http.secured.post(this.accountingNamespace+'/invoices/confirmation-images/'+this.id+'/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          .then(response => {
            console.log(response.data);
            this.getInvoiceImages()
          })
          .catch(error => {
            if (error.response) {
              this.alerMessage.messages = error.response.data.messages
              this.alerMessage.show = true
            }else{
              this.showMessage("Something went wrong! Please try again.", 'error')
            }
          })
          .then(function () {
            self.imgUploading = false
          })
      },
      getShipmentIcon (item) {
        if(item.shipment_type){
          return this.isShipmentForSea(item.shipment_type) ? 'mdi-ferry' : 'mdi-airplane'
        }else{
          return ''
        }
      },
      doEdit () {
        this.$router.push({ name: 'invoice', params: { id: this.id } })
      },
      isShipmentForSea (shipment_type){
        return this.forSea(shipment_type, this)
      },
      doClose (){
        this.$router.push({ name: 'invoices' })
      },
      paymentMade (invoice){
        this.dialog = {
          show: true,
          title: 'Invoice Paid',
          body: 'Are you sure payment has been made for invoice '+invoice.invoice_no+'?',
          type: 'confirm-payment',
          item: invoice,
          confirmBtn: 'Yes',
          cancelBtn: 'No'
        }
      },
      paid (){
        self.loading = true

        this.$http.secured.post(this.accountingNamespace+'/invoices/paid/'+this.id, '')
          .then(response => {
            this.invoice.status = this.$getConstInvoiceStatus('INVOICE_STATUS_PAID')
            this.actionBtnSettings.edit = 0
            this.showMessage("Payment has been posted.", 'success')
          })
          .catch(error => {
            if (error.response) {
              this.showMessage(error.response.data.messages, 'error')
            }else{
              this.showMessage("Something went wrong! Please try again.", 'error')
            }
          })
          .then(function () {
            self.loading = false
          })
      },
      doConfirm () {
        this.dialog.show = false
        if(this.dialog.type == 'confirm-payment'){
          this.paid()
        }else if(this.dialog.type == 'confirm-remove-img'){
          this.deleteImg()
        }
      },
      doCancel () {
        this.dialog.show = false
      },
      doPrint (){
        this.print = true
        this.getInvoice()
      },
      getInvoice () {
        if(this.print){
          const currentDate = new Date();
          const unixTimestamp = Math.floor(currentDate.getTime() / 1000)

          window.open('/api/'+this.accountingNamespace+'/invoices/'+this.id+'?print=true&v='+unixTimestamp, '_newtab');
          return false
        }
        let self = this
        this.loading = true
        this.$http.plain.get(this.accountingNamespace+'/invoices/'+this.id)
          .then(response => {
            if(!this.print){
              this.invoice = response.data
              this.items = this.invoice.items
              if(this.invoice.status == this.$getConstInvoiceStatus('INVOICE_STATUS_CREATED') ||
                this.invoice.status == this.$getConstInvoiceStatus('INVOICE_STATUS_SENT')){
                this.actionBtnSettings.edit = 1
              }else{
                this.actionBtnSettings.edit = 0
              }
            }
          })
          .catch(error => {
            console.log(error);
          })
          .then(function () {
            self.loading = false
            self.print = false
          })
      },
      getDefaultData () {
        let self = this
        this.$http.plain.get('/default-data?invoice_statuses=true')
          .then(response => {
            this.statuses = response.data.invoice_statuses
          })
          .catch(error => {
            console.log(error);
          })
      },
      showMessage(msg, color){
        this.snackbarDetails = {
          color: color,
          msg: msg,
          show: true
        }
      }
    },
    mounted() {
      if(this.$route.params.id != 'add'){
        this.id = this.$route.params.id
      }
      this.getDefaultData()
      this.getInvoice()
      this.getInvoiceImages()
    },
    components: {
      SnackBar,
      Loader,
      ActionButtons,
      InvoiceStatus,
      ConfirmationDialog,
      AlertMessage
    }
  }
</script>
